'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';
import { useTypingAnimation } from '../_hooks/useTypingAnimation';
import type { ChatMessage as ChatMessageType } from '../_lib/types';
interface ChatMessageProps {
  message: ChatMessageType;
  isLatest?: boolean;
  showAvatar?: boolean;
  showStartButton?: boolean;
  onStart?: () => void;
  onSelectOption?: (value: string) => void;
  onScrollToBottom?: () => void;
}
const formatTime = (date: Date) => {
  const h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, '0');
  return `${h >= 12 ? '오후' : '오전'} ${h > 12 ? h - 12 : h || 12}:${m}`;
};
const WelloAvatar = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="relative size-48 shrink-0"><Image alt={"\uC6F0\uB85C \uC815\uCC45 \uB9E4\uCE6D\uBD07"} className="rounded-pill" height={48} src={`${WELLO_IMAGE_PREFIX_URL}/static/wello-l.png`} width={48} /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const ChatMessage = t0 => {
  const $ = _c(59);
  const {
    message,
    isLatest,
    showAvatar: t1,
    showStartButton,
    onStart,
    onSelectOption,
    onScrollToBottom
  } = t0;
  const showAvatar = t1 === undefined ? true : t1;
  const shouldAnimate = isLatest && message.type === "bot";
  let t2;
  if ($[0] !== message.content || $[1] !== shouldAnimate) {
    t2 = {
      text: message.content,
      speed: 20,
      delay: 100,
      play: shouldAnimate
    };
    $[0] = message.content;
    $[1] = shouldAnimate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    displayedText,
    isComplete
  } = useTypingAnimation(t2);
  let t3;
  if ($[3] !== onScrollToBottom || $[4] !== shouldAnimate) {
    t3 = () => {
      if (shouldAnimate) {
        onScrollToBottom?.();
      }
    };
    $[3] = onScrollToBottom;
    $[4] = shouldAnimate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== displayedText || $[7] !== isComplete || $[8] !== onScrollToBottom || $[9] !== shouldAnimate) {
    t4 = [displayedText, isComplete, shouldAnimate, onScrollToBottom];
    $[6] = displayedText;
    $[7] = isComplete;
    $[8] = onScrollToBottom;
    $[9] = shouldAnimate;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  if (message.type === "user") {
    let t5;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        opacity: 1,
        y: 0
      };
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        opacity: 0,
        y: 10
      };
      t7 = {
        duration: 0.3
      };
      $[12] = t6;
      $[13] = t7;
    } else {
      t6 = $[12];
      t7 = $[13];
    }
    let t8;
    if ($[14] !== message.timestamp) {
      t8 = formatTime(message.timestamp);
      $[14] = message.timestamp;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== t8) {
      t9 = <span className="pb-4 text-Gray-500 typo-caption2">{t8}</span>;
      $[16] = t8;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== message.content) {
      t10 = <div className="max-w-280 rounded-b-large rounded-tl-large rounded-tr-xxsmall bg-B-500 p-16"><p className="leading-normal text-Gray-white typo-body4">{message.content}</p></div>;
      $[18] = message.content;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== t10 || $[21] !== t9) {
      t11 = <motion.div animate={t5} className="mt-16 flex justify-end" initial={t6} transition={t7}><div className="flex items-end gap-8">{t9}{t10}</div></motion.div>;
      $[20] = t10;
      $[21] = t9;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    return t11;
  }
  let t5;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 1,
      y: 0
    };
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const t6 = showAvatar ? "mt-16" : "mt-8";
  let t7;
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 10
    };
    t8 = {
      duration: 0.3
    };
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  let t9;
  if ($[26] !== showAvatar) {
    t9 = showAvatar && <div className="mb-8"><WelloAvatar /></div>;
    $[26] = showAvatar;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const t10 = shouldAnimate ? displayedText : message.content;
  let t11;
  if ($[28] !== isComplete || $[29] !== shouldAnimate) {
    t11 = shouldAnimate && !isComplete && <span className="ml-1 inline-block h-14 w-2 animate-pulse bg-Gray-800" />;
    $[28] = isComplete;
    $[29] = shouldAnimate;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = <p className="whitespace-pre-line leading-normal text-Gray-800 typo-body4">{t10}{t11}</p>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== isComplete || $[35] !== onStart || $[36] !== showStartButton) {
    t13 = showStartButton && isComplete && <motion.button animate={{
      opacity: 1,
      y: 0
    }} className="mt-16 h-40 w-full rounded-xsmall bg-Gray-900 px-16 py-10 font-700 text-Gray-white transition-all typo-body4 hover:bg-Gray-800 active:scale-[0.98]" initial={{
      opacity: 0,
      y: 5
    }} transition={{
      delay: 0.1
    }} onClick={onStart}>시작하기</motion.button>;
    $[34] = isComplete;
    $[35] = onStart;
    $[36] = showStartButton;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== isComplete || $[39] !== isLatest || $[40] !== message.options || $[41] !== onSelectOption || $[42] !== showStartButton) {
    t14 = isLatest && message.options && !showStartButton && isComplete && <motion.div animate={{
      opacity: 1,
      y: 0
    }} className="mt-16 flex flex-col gap-8" initial={{
      opacity: 0,
      y: 5
    }} transition={{
      delay: 0.1
    }}>{message.options.map(option => <button type="button" key={option.value} className="h-40 w-full rounded-xsmall bg-Gray-200 px-16 py-10 text-center font-700 text-Gray-900 transition-all typo-body4 hover:bg-Gray-300 active:scale-[0.98]" onClick={() => onSelectOption?.(option.value)}>{option.label}</button>)}</motion.div>;
    $[38] = isComplete;
    $[39] = isLatest;
    $[40] = message.options;
    $[41] = onSelectOption;
    $[42] = showStartButton;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t12 || $[45] !== t13 || $[46] !== t14) {
    t15 = <div className="max-w-280 rounded-r-large rounded-bl-large rounded-tl-xxsmall bg-Gray-white p-16">{t12}{t13}{t14}</div>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== message.timestamp) {
    t16 = formatTime(message.timestamp);
    $[48] = message.timestamp;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t16) {
    t17 = <span className="pb-4 text-Gray-500 typo-caption2">{t16}</span>;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t15 || $[53] !== t17) {
    t18 = <div className="flex items-end gap-8">{t15}{t17}</div>;
    $[52] = t15;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t18 || $[56] !== t6 || $[57] !== t9) {
    t19 = <motion.div animate={t5} className={t6} initial={t7} transition={t8}>{t9}{t18}</motion.div>;
    $[55] = t18;
    $[56] = t6;
    $[57] = t9;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
};